import {
  builtinToolCatalog,
  type BuiltinToolName,
  ToolNames,
} from '@kanbu/schema/contracts/tools';
import { useLingui } from '@lingui/react/macro';

/**
 * Read-only catalog of built-in tools maintained by Kanbu. No create/edit/
 * delete — these are the core product capabilities every other tool source
 * builds on.
 */
export function BuiltinToolsTab() {
  const { t } = useLingui();
  const localizedCopy: Record<
    BuiltinToolName,
    { displayName: string; description: string }
  > = {
    [ToolNames.SearchPdf]: {
      displayName: t`Search PDF`,
      description: t`Searches text in a PDF URL using multiple phrases and returns passages with page numbers. Does not run OCR; use View PDF Page for scanned content. For knowledge-base documents, use Document Search.`,
    },
    [ToolNames.ViewPage]: {
      displayName: t`View PDF Page`,
      description: t`Inspects a PDF page or figure crop from an authorized knowledge-base document or allowed URL. Available in text chats with image-capable models. Shares the five-image turn limit with See Image.`,
    },
    [ToolNames.SeeImage]: {
      displayName: t`See Image`,
      description: t`Inspects images visually to answer questions about diagrams, charts and photographs. Uses the same allowed domains as Fetch URL. Available in text chats.`,
    },
    [ToolNames.RecordAnswerSources]: {
      displayName: t`Voice: Record answer sources`,
      description: t`Optional voice source attribution. Adds a model tool call and may increase latency. Disabled by default.`,
    },
    [ToolNames.DocumentSearch]: {
      displayName: t`Document Search`,
      description: t`Searches the agent knowledge base (RAG) to answer questions from uploaded documents and synced content.`,
    },
    [ToolNames.FindDocuments]: {
      displayName: t`Find Documents`,
      description: t`Answers "what do you have about X" at the level of whole documents — titles, summaries and section outlines, no passages. Worth enabling on a large knowledge base, where the agent can pick the right document first and then search inside it; on a handful of documents it only adds a round-trip.`,
    },
    [ToolNames.GetDocumentOverview]: {
      displayName: t`Document Overview`,
      description: t`Reads one document's full section outline and its long summary, for questions about what a document covers rather than what it says on a point. Requires Document Search alongside it to quote anything specific.`,
    },
    [ToolNames.WebSearch]: {
      displayName: t`Web Search`,
      description: t`Searches the web, restricted to the allowed domains configured for the agent, for up-to-date external information.`,
    },
    [ToolNames.FetchUrl]: {
      displayName: t`Fetch URL`,
      description: t`Reads the content of a specific web page or online document — PDF, Word, PowerPoint, images and spreadsheets are converted to text (long documents are read in pages) — and verifies that links resolve before sharing them.`,
    },
    [ToolNames.GetOperator]: {
      displayName: t`Operator Handoff`,
      description: t`Escalates the conversation to a human operator when the agent cannot resolve the request.`,
    },
    [ToolNames.GetShopProducts]: {
      displayName: t`Shop: Search Products`,
      description: t`Searches the product catalog by query to recommend and present matching products.`,
    },
    [ToolNames.GetSimilarProducts]: {
      displayName: t`Shop: Similar Products`,
      description: t`Finds the products closest to a given one in the catalog — alternatives to browse, or a replacement when the chosen item is unavailable.`,
    },
    [ToolNames.GetShopProductDetails]: {
      displayName: t`Shop: Product Details`,
      description: t`Loads the full record of up to four known products — by internal ID, or by external ID, EAN or ISBN — including description, parameters, images, gifts, accessories and availability. Also used to compare products.`,
    },
    [ToolNames.GetShopCategories]: {
      displayName: t`Shop: Categories`,
      description: t`Lists shop categories to help users browse the catalog.`,
    },
    [ToolNames.GetShopDeliveryOptions]: {
      displayName: t`Shop: Delivery Options`,
      description: t`Returns available delivery and shipping options.`,
    },
    [ToolNames.RecommendShopProducts]: {
      displayName: t`Shop: Recommend Products`,
      description: t`Presents agent-selected products to the user as product cards. Card data is loaded from the catalog by product ID, so the agent cannot invent products or links.`,
    },
    [ToolNames.ShowShopProductDetail]: {
      displayName: t`Shop: Show Product Detail`,
      description: t`Presents the full detail card of one product (images, parameters, categories) to the user. Data is loaded from the catalog by product ID.`,
    },
    [ToolNames.UpdateWorkingMemory]: {
      displayName: t`Working Memory`,
      description: t`Lets the agent keep a short per-conversation scratchpad of facts, constraints and open questions, so details established in earlier turns survive instead of being re-invented.`,
    },
    [ToolNames.TransferCall]: {
      displayName: t`Transfer Call`,
      description: t`Hands a live phone call over to a human operator (cold transfer). Voice calls only — the agent drops off once the caller is connected.`,
    },
    [ToolNames.EndCall]: {
      displayName: t`End Call`,
      description: t`Ends a live phone call once the conversation is genuinely complete, instead of leaving the line open until the caller hangs up.`,
    },
  };

  return (
    <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
      {builtinToolCatalog.map(tool => {
        const copy = localizedCopy[tool.name];

        return (
          <div
            key={tool.name}
            className='bg-card flex flex-col rounded-radius border border-border p-4'
          >
            <h3 className='text-sm font-medium'>{copy.displayName}</h3>
            <p className='mt-1 flex-1 text-sm text-muted-fg'>
              {copy.description}
            </p>
          </div>
        );
      })}
    </div>
  );
}
